import React, { useState, useEffect } from 'react';
import { manufacturingAPI } from '../../../services/api';
import './LoadBOMModal.css';

const LoadBOMModal = ({ onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Load items with children (items that have BOMs)
  useEffect(() => {
    loadItemsWithBOM();
  }, []);

  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length >= 2) {
        searchItems();
      } else if (searchTerm.length === 0) {
        loadItemsWithBOM();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadItemsWithBOM = async () => {
    setLoading(true);
    try {
      const response = await manufacturingAPI.bom.parentItems({
        page_size: 50,
      });
      setItems(response.data);  // Direct array, no .results
    } catch (err) {
      console.error('Failed to load items with BOM:', err);
    } finally {
      setLoading(false);
    }
  };

  const searchItems = async () => {
    setLoading(true);
    try {
      const response = await manufacturingAPI.items.list({
        search: searchTerm,
        page_size: 50,
      });
      setItems(response.data.results || response.data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = () => {
    if (selectedItem) {
      onSelect(selectedItem);
    }
  };

  const getTypeColor = (type, isPhantom) => {
    if (isPhantom) return '#f59e0b';
    switch (type) {
      case 'finished_good': return '#22c55e';
      case 'assembly': return '#3b82f6';
      case 'manufactured': return '#8b5cf6';
      case 'raw_material': return '#64748b';
      default: return '#94a3b8';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'finished_good': return 'Finished Good';
      case 'assembly': return 'Assembly';
      case 'manufactured': return 'Manufactured';
      case 'raw_material': return 'Raw Material';
      default: return type || 'Unknown';
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="load-bom-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2>Load Existing BOM</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="modal-search">
          <input
            type="text"
            placeholder="Search by item code or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        {/* Instructions */}
        <div className="modal-instructions">
          Select an item to load its complete BOM tree onto the canvas
        </div>

        {/* Items List */}
        <div className="modal-items-list">
          {loading && (
            <div className="modal-loading">
              <div className="spinner"></div>
              Loading items...
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="modal-empty">
              No items found
            </div>
          )}

          {!loading && items.map((item) => (
            <div
              key={item.id}
              className={`modal-item ${selectedItem?.id === item.id ? 'selected' : ''}`}
              onClick={() => setSelectedItem(item)}
              onDoubleClick={() => {
                setSelectedItem(item);
                onSelect(item);
              }}
            >
              <div 
                className="item-type-bar"
                style={{ backgroundColor: getTypeColor(item.item_type, item.is_phantom) }}
              />
              <div className="item-details">
                <div className="item-code">{item.item_code}</div>
                <div className="item-name">{item.name}</div>
                <div className="item-meta">
                  <span className="item-type">{getTypeLabel(item.item_type)}</span>
                  {item.is_phantom && <span className="phantom-tag">Phantom</span>}
                  {item.bom_count && <span className="bom-count">{item.bom_count} components</span>}
                </div>
              </div>
              {selectedItem?.id === item.id && (
                <div className="check-mark">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>
            Cancel
          </button>
          <button 
            className="btn-load"
            onClick={handleSelect}
            disabled={!selectedItem}
          >
            Load BOM
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoadBOMModal;